import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Menu,
  Box,
  TextField,
  InputAdornment,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Radio,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";

const NAVY = "#0D3B66";
const SELECTED_BG = "#EEF3FF";
const SELECTED_BORDER = "#B9CBFB";

export default function SelectDashboardFileMenu({
  anchorEl,
  open,
  onClose,
  files = [],
  selectedFileId,
  onApply,
  loading = false,
  loadingMore = false,
  hasMore = true,
  onLoadMore,
  onSearch,
}) {
  const [query, setQuery] = useState("");
  const [pendingId, setPendingId] = useState(selectedFileId || null);
  const listboxRef = useRef(null);

  const getFileName = (file) => {
    if (!file) return "";
    if (Array.isArray(file.file_name)) return file.file_name[0] || "";
    if (typeof file.file_name === "string") return file.file_name;
    if (Array.isArray(file.fileName)) return file.fileName[0] || "";
    if (typeof file.fileName === "string") return file.fileName;
    if (typeof file.name === "string") return file.name;
    return String(file.file_name || file.fileName || file.name || "");
  };

  const getLoadId = (file) => file?.load_id || file?.loadId || "";

  const getDateRange = (file) => {
    if (!file) return "";
    if (file.dateRange) return file.dateRange;
    if (file.start_date && file.end_date) return `${file.start_date} - ${file.end_date}`;
    if (file.start_date) return file.start_date;
    return "";
  };

  const getFileId = (file) => file.header_id || file.id || file.load_id || Math.random();

  const filtered = files; // server-side search — files already reflects the applied search term

  const debouncedSearchRef = useRef(debounce((value) => onSearch?.(value), 400));

  useEffect(() => {
    return () => debouncedSearchRef.current.cancel();
  }, []);

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearchRef.current(value);
  };

  const handleScroll = useCallback(
    (event) => {
      const node = event.currentTarget;
      const reachedBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 20;
      if (reachedBottom && hasMore && !loadingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [hasMore, loadingMore, onLoadMore],
  );

  useEffect(() => {
    if (open) {
      setPendingId(selectedFileId || null);
      setQuery("");
      debouncedSearchRef.current.cancel();
      if (listboxRef.current) listboxRef.current.scrollTop = 0;
    }
  }, [open, selectedFileId]);

  const handleApply = () => {
    onApply?.(pendingId);
    onClose?.();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { width: 320, borderRadius: 2, mt: 0.5, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden" } }}
    >
      {/* Header band */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: "#F1F5F9",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
          Select a file to generate the dashboard
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, pt: 1.25}}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search files"
          value={query}
          onChange={handleQueryChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { fontSize: 13, borderRadius: 1.5,border:"1.5px solid #E2E8F0" } }}
        />
      </Box>

      <Box
        ref={listboxRef}
        onScroll={handleScroll}
        sx={{
          maxHeight: 300,
          overflowY: "auto",
          px: 1.5,
          pt: 1.25,
          pb: 0.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 3 },
        }}
      >
        {loading && filtered.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: "#94A3B8", textAlign: "center", py: 3 }}>
            No files found
          </Typography>
        ) : (
          <>
            {filtered.map((file) => {
              const fileId = getFileId(file);
              const fileName = getFileName(file);
              const loadId = getLoadId(file);
              const dateRange = getDateRange(file);
              const checked = String(pendingId) === String(fileId);

              return (
                <Box
                  key={fileId}
                  onClick={() => setPendingId(fileId)}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    border: `1.5px solid ${checked ? SELECTED_BORDER : "#E2E8F0"}`,
                    borderRadius: 1.5,
                    p: 1.1,
                    cursor: "pointer",
                    bgcolor: checked ? SELECTED_BG : "#fff",
                    transition: "background-color 0.15s ease, border-color 0.15s ease",
                    "&:hover": { bgcolor: checked ? SELECTED_BG : "#F8FAFC" },
                  }}
                >
                  <Radio
                    size="small"
                    checked={checked}
                    onChange={() => setPendingId(fileId)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      p: 0.25,
                      mt: 0.15,
                      color: "#CBD5E1",
                      "&.Mui-checked": { color: NAVY },
                      "& .MuiSvgIcon-root": { fontSize: 21 },
                    }}
                  />

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {dateRange && (
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 500, color: "#334155", lineHeight: 1.3 }}
                      >
                        {dateRange}
                      </Typography>
                    )}

                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: "#94A3B8",
                        mt: dateRange ? 0.25 : 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={fileName}
                    >
                      File: {fileName}
                    </Typography>

                    {loadId && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 400,
                          color: "#94A3B8",
                          mt: 0.25,
                          wordBreak: "break-all",
                          lineHeight: 1.4,
                        }}
                      >
                        Load ID: {loadId}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}

            {loadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={16} />
              </Box>
            )}

            {!hasMore && filtered.length > 0 && (
              <Typography sx={{ fontSize: 11, color: "#94A3B8", textAlign: "center", py: 1 }}>
                No more files
              </Typography>
            )}
          </>
        )}
      </Box>

      <Divider sx={{ mt: 1, borderColor: "#E2E8F0" }} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2, py: 1.25 }}>
        <Button
          size="small"
          onClick={onClose}
          sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 600, color: "#64748B" }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!pendingId}
          onClick={handleApply}
          sx={{
            textTransform: "none",
            fontSize: 12.5,
            fontWeight: 600,
            bgcolor: NAVY,
            boxShadow: "none",
            "&:hover": { bgcolor: "#0D47A1", boxShadow: "none" },
          }}
        >
          Select
        </Button>
      </Box>
    </Menu>
  );
}